/**
 * Ambient POIs — the places already inside the basemap tiles, drawn as quiet
 * context under the block's authored pins.
 *
 * OpenFreeMap's planet tileset ships sixteen vector layers and the shipped
 * style draws eleven; `poi` is one of the five it downloads and never renders.
 * So this costs no new provider, no key and not one extra request — it is a
 * layer over bytes the map already has.
 *
 * ── Why the curation is not optional ─────────────────────────────────────────
 * Measured, not assumed: six z14 tiles (Berlin Mitte, Paris, Shinjuku, NYC
 * Midtown, Tel Aviv, Kreuzberg) carry 37,475 poi features, and the LARGEST
 * classes are street furniture — 3,143 bicycle_parking, 2,795 bollard, 1,772
 * waste_basket, 1,357 gate. Drawing the layer as it ships is visual mud that
 * says nothing. Three filters cut it to context a reader can use:
 *
 *   1. class ∈ AMBIENT_POI_CLASSES — destinations only, from the observed
 *      population (98 distinct classes were seen; 27 are kept).
 *   2. the feature is named — 16,220 of those 37,475 have no name at all, and
 *      an unnamed dot is noise the click popup cannot even answer for.
 *   3. rank <= AMBIENT_POI_MAX_RANK — the tiles' own per-tile importance
 *      ordering. Paris alone holds 7,412 curated named POIs in one tile; the
 *      rank cap and the collision index below are what keep that legible.
 *
 * Visual thinning is MapLibre's, not ours: the layer is a symbol layer with
 * collision on and `symbol-sort-key` = rank, so the most important POIs that
 * fit are the ones drawn, at every zoom, and zooming in reveals more. That is
 * also why the labels are `text-optional` — where a name cannot fit, the dot
 * still marks the place.
 *
 * ── Why our own dot rather than the sprite's icons ───────────────────────────
 * The style's sprite does carry maki-style icons named after these very
 * classes, but every entry reports `sdf: undefined` — they cannot be tinted to
 * our cartography, and their own iconography would collide with the Tabler
 * glyph language the authored pins speak. A pin is the block's content; an
 * ambient POI is the neighbourhood behind it, and the two must never read
 * alike. So ambient POIs are dots in our ink, drawn at runtime.
 *
 * Kept React-free and DOM-free at module scope — `document` is touched only
 * inside `ensureDotImages`, so `map-ambient-poi.assert.ts` can check the
 * curation and the layer shape without a browser.
 */

type MapLibreModule = typeof import('maplibre-gl');
type MapLibreMapInstance = InstanceType<MapLibreModule['Map']>;
type StyleLayer = import('maplibre-gl').LayerSpecification;
type SymbolLayer = import('maplibre-gl').SymbolLayerSpecification;
type StyleExpression = import('maplibre-gl').ExpressionSpecification;
type FilterSpec = import('maplibre-gl').FilterSpecification;

/** The tiles' own layer and fields, from the OpenMapTiles schema. */
const OMT_POI_SOURCE_LAYER = 'poi';
const OMT_PLACE_SOURCE_LAYER = 'place';
const OMT_TRANSPORTATION_NAME_SOURCE_LAYER = 'transportation_name';
const OMT_CLASS_FIELD = 'class';
const OMT_RANK_FIELD = 'rank';
const OMT_NAME_FIELD = 'name';
/** The latin transliteration the shipped style prefers for its own labels;
 *  20,029 of the 21,255 named features carry it, and `name` covers the rest. */
const OMT_NAME_LATIN_FIELD = 'name:latin';

/** Expression operators and layer-spec discriminants, spelled once. */
const EXPRESSION_GET = 'get';
const EXPRESSION_HAS = 'has';
const EXPRESSION_IN = 'in';
const EXPRESSION_ALL = 'all';
const EXPRESSION_LESS_OR_EQUAL = '<=';
const EXPRESSION_LITERAL = 'literal';
const EXPRESSION_COALESCE = 'coalesce';
const EXPRESSION_INTERPOLATE = 'interpolate';
const EXPRESSION_LINEAR = 'linear';
const EXPRESSION_ZOOM = 'zoom';
const EXPRESSION_MATCH = 'match';
const LAYER_TYPE_SYMBOL = 'symbol';
const SOURCE_LAYER_PROPERTY = 'source-layer';

/** Our layer, and the images it draws. Prefixed so nothing can collide with a
 *  vendor style's own ids. */
export const MAP_AMBIENT_POI_LAYER_ID = 'cabal-ambient-poi';
const AMBIENT_POI_IMAGE_PREFIX = 'cabal-ambient-poi-dot';

/**
 * How far in the reader must be before ambient POIs appear.
 *
 * z14, because that is the first zoom whose tiles carry PLACES at all. A z13
 * tile is not a thinner version of z14: it is a different subject. Measured
 * across four cities, every curated z13 feature is transit infrastructure —
 * Kreuzberg 25 of 25, Berlin 56 of 56, Paris 108 of 108, NYC 71 of 71, all
 * railway and ferry terminals — and those duplicate hard, because a station's
 * every entrance is its own feature ("23rd Street" appears five times in one
 * NYC tile, "Hallesches Tor" three times over Kreuzberg). A floor of 13 was
 * briefly tried and it turned this feature into a U-Bahn map with repeated
 * labels, which is neither what it is for nor something a reader would call
 * correct. From z14 the mix inverts to what the feature is actually about:
 * shops, restaurants, cafés, bars and hotels, with transit down at 1-4%.
 *
 * The cost is stated rather than hidden: a block fitting several pins lands
 * below this floor (five Kreuzberg pins fit at z13.3) and shows no ambient
 * places until the reader zooms one step. A SINGLE-pin block — every location
 * and hotel card map — fits at `MAP_FIT_MAX_ZOOM`, which is exactly 14, so
 * the surface where "what is around this place" is the actual question is the
 * surface that answers it on arrival.
 */
export const AMBIENT_POI_MIN_ZOOM = 14;

/**
 * The rank ceiling. Curated named POIs across the six probe tiles run rank
 * p25=42, p50=92, p90=276, so this keeps roughly the more-important half and
 * bounds the symbol set a dense city hands the collision index.
 */
const AMBIENT_POI_MAX_RANK = 100;

/** What a place IS, in the reader's terms. The groups exist for the category
 *  treatment's ink and for the popup's kind line. */
export enum MapPoiCategory {
  Food = 'food',
  Stay = 'stay',
  Culture = 'culture',
  Shop = 'shop',
  Service = 'service',
  Transit = 'transit',
  Green = 'green',
}

/**
 * The kept classes, grouped. Every value here was OBSERVED in the probe
 * population — none is copied from a schema table — and each appears in
 * exactly one group, which `map-ambient-poi.assert.ts` pins.
 */
const CLASSES_BY_CATEGORY: Record<MapPoiCategory, readonly string[]> = {
  [MapPoiCategory.Food]: [
    'restaurant',
    'cafe',
    'fast_food',
    'bar',
    'beer',
    'ice_cream',
    'bakery',
  ],
  [MapPoiCategory.Stay]: ['lodging'],
  [MapPoiCategory.Culture]: [
    'museum',
    'art_gallery',
    'theatre',
    'cinema',
    'attraction',
    'monument',
    'castle',
  ],
  [MapPoiCategory.Shop]: [
    'shop',
    'clothing_store',
    'grocery',
    'alcohol_shop',
    'butcher',
  ],
  [MapPoiCategory.Service]: ['pharmacy', 'bank', 'hospital'],
  [MapPoiCategory.Transit]: ['railway', 'ferry_terminal', 'harbor'],
  [MapPoiCategory.Green]: ['park'],
};

/** Derived so the filter and the category ink can never name different sets. */
export const AMBIENT_POI_CLASSES: readonly string[] = Object.values(
  CLASSES_BY_CATEGORY,
).flat();

/**
 * The kind line a click popup prints. Deliberately the CLASS and not the
 * subclass: subclass is a raw OSM tag with hundreds of values nobody has
 * enumerated, and a humanised guess at one is exactly the inference this
 * codebase does not ship. A class with no entry prints nothing rather than a
 * machine token.
 */
const KIND_LABEL_BY_CLASS: Readonly<Record<string, string>> = {
  restaurant: 'Restaurant',
  cafe: 'Café',
  fast_food: 'Fast food',
  bar: 'Bar',
  beer: 'Beer',
  ice_cream: 'Ice cream',
  bakery: 'Bakery',
  lodging: 'Hotel',
  museum: 'Museum',
  art_gallery: 'Gallery',
  theatre: 'Theatre',
  cinema: 'Cinema',
  attraction: 'Attraction',
  monument: 'Monument',
  castle: 'Castle',
  shop: 'Shop',
  clothing_store: 'Clothing',
  grocery: 'Grocery',
  alcohol_shop: 'Drinks',
  butcher: 'Butcher',
  pharmacy: 'Pharmacy',
  bank: 'Bank',
  hospital: 'Hospital',
  railway: 'Station',
  ferry_terminal: 'Ferry',
  harbor: 'Harbour',
  park: 'Park',
};

/**
 * The at-rest ink of an ambient dot.
 *
 * One muted slate for the plain treatments: ambient POIs sit UNDER the mint
 * authored pins in every reading, so they never take the accent. The category
 * hues are the same idea held at low chroma — enough to tell food from a
 * museum at a glance, never enough to compete with a pin.
 */
const AMBIENT_DOT_NEUTRAL_COLOR = '#8fa0ad';
const AMBIENT_DOT_COLOR_BY_CATEGORY: Record<MapPoiCategory, string> = {
  [MapPoiCategory.Food]: '#e0a86a',
  [MapPoiCategory.Stay]: '#7fa9e0',
  [MapPoiCategory.Culture]: '#c08fd6',
  [MapPoiCategory.Shop]: '#8fa0ad',
  [MapPoiCategory.Service]: '#e08f96',
  [MapPoiCategory.Transit]: '#7fc7c9',
  [MapPoiCategory.Green]: '#7fc79a',
};

/** The dot's dark rim — the same near-black the Sovereign label halo uses, so
 *  a dot separates from land, water and road alike. */
const AMBIENT_DOT_RIM_COLOR = 'rgba(5, 7, 10, 0.92)';

/** The label ink. Below the basemap's own place names (white / accent) on
 *  purpose: an ambient name is the quietest text on the canvas. */
const AMBIENT_LABEL_COLOR = 'rgba(255, 255, 255, 0.62)';
const AMBIENT_LABEL_HALO_COLOR = '#05070a';
const AMBIENT_LABEL_HALO_WIDTH = 1.2;
/**
 * The GLYPH STACK the tile server rasterises canvas labels from — not an app
 * font, and not a CSS `font-family`: it names the `.pbf` glyph ranges under
 * the style's own `glyphs` endpoint, and every basemap label already on screen
 * is drawn with it. The style ships exactly this one stack, and a name it
 * cannot serve renders no label at all rather than falling back, so this is
 * the value or nothing. The app-font rule cannot see that distinction, hence
 * the scoped exemption; the CABAL Avenir stack is untouched, on this surface
 * and everywhere else.
 */
// eslint-disable-next-line local-rules/no-non-system-font-family
const AMBIENT_LABEL_FONT: readonly string[] = ['Noto Sans Regular'];
/**
 * Label geometry, sized from the real names rather than from a guess: curated
 * POI names run 11 characters at the median, 21 at p90 and 36 at p99. At this
 * text size the cap wraps a p90 name onto a second line and leaves the p99
 * outlier to the collision index, which drops it before it can crowd the map.
 */
const AMBIENT_LABEL_MAX_WIDTH_EMS = 8;
const AMBIENT_LABEL_ANCHOR = 'top';
const AMBIENT_LABEL_OFFSET: readonly [number, number] = [0, 0.7];

/** The at-rest treatment of the tiles' own places. */
export enum MapAmbientPoiVariant {
  /** No ambient POIs — the basemap as it shipped. */
  Off = 'Off',
  /** Dots only. Pure texture: the reader sees WHERE things are, and asks the
   *  click popup what they are. */
  Dots = 'Quiet dots',
  /** The shipped pick: dots with their names, where a name fits. */
  Named = 'Named',
  /** Named, with each dot tinted by what kind of place it is. */
  CategoryInk = 'Category ink',
}

export const MAP_AMBIENT_POI_VARIANTS: readonly MapAmbientPoiVariant[] = [
  MapAmbientPoiVariant.Named,
  MapAmbientPoiVariant.Dots,
  MapAmbientPoiVariant.CategoryInk,
  MapAmbientPoiVariant.Off,
];

/** What a map with no control over it draws. */
export const MAP_AMBIENT_POI_DEFAULT = MapAmbientPoiVariant.Named;

/** Whether a treatment prints names beside its dots. */
const VARIANT_DRAWS_LABELS: Record<MapAmbientPoiVariant, boolean> = {
  [MapAmbientPoiVariant.Off]: false,
  [MapAmbientPoiVariant.Dots]: false,
  [MapAmbientPoiVariant.Named]: true,
  [MapAmbientPoiVariant.CategoryInk]: true,
};

/** Whether a treatment tints its dots by category. */
const VARIANT_TINTS_BY_CATEGORY: Record<MapAmbientPoiVariant, boolean> = {
  [MapAmbientPoiVariant.Off]: false,
  [MapAmbientPoiVariant.Dots]: false,
  [MapAmbientPoiVariant.Named]: false,
  [MapAmbientPoiVariant.CategoryInk]: true,
};

export interface AmbientPoiText {
  name: string;
  /** The class's reader-facing kind, or null for a class with no entry. */
  kind: string | null;
}

/**
 * What a clicked ambient POI says. Returns null when the feature carries no
 * usable name — the filter already excludes those, so this is the guard for a
 * style or schema that hands back something else.
 */
export function ambientPoiText(
  properties: Readonly<Record<string, unknown>>,
): AmbientPoiText | null {
  const latin = properties[OMT_NAME_LATIN_FIELD];
  const plain = properties[OMT_NAME_FIELD];
  const name =
    typeof latin === 'string' && latin.length > 0
      ? latin
      : typeof plain === 'string' && plain.length > 0
        ? plain
        : null;
  if (name == null) {
    return null;
  }

  const className = properties[OMT_CLASS_FIELD];
  const kind =
    typeof className === 'string' ? (KIND_LABEL_BY_CLASS[className] ?? null) : null;

  return { name, kind };
}

/** The image id one category's dot is registered under. */
function dotImageId(category: MapPoiCategory | null): string {
  return category == null
    ? AMBIENT_POI_IMAGE_PREFIX
    : `${AMBIENT_POI_IMAGE_PREFIX}--${category}`;
}

/** Category of a kept class, or null for a class outside the curation. */
export function ambientPoiCategory(className: string): MapPoiCategory | null {
  const found = Object.entries(CLASSES_BY_CATEGORY).find(([, classNames]) =>
    classNames.includes(className),
  );
  return found ? (found[0] as MapPoiCategory) : null;
}

/* ── The layer ────────────────────────────────────────────────────────────── */

const EXPRESSION_NOT = '!';

/**
 * Only the curated, named, reasonably-ranked places — and never a place the
 * block already authored a pin for.
 *
 * That last clause is not tidiness. A Kreuzberg frame drew the basemap's own
 * "Defne" as a quiet ambient label while the block's authored "Defne" pin and
 * its rail card named the same restaurant twice over, which reads as two
 * places rather than as one place said twice. The block's pin is the content
 * and always wins; the tiles' copy of it steps aside.
 */
function ambientPoiFilter(authoredNames: readonly string[]): FilterSpec {
  const clauses: unknown[] = [
    [
      EXPRESSION_IN,
      [EXPRESSION_GET, OMT_CLASS_FIELD],
      [EXPRESSION_LITERAL, AMBIENT_POI_CLASSES],
    ],
    [EXPRESSION_HAS, OMT_NAME_FIELD],
    [
      EXPRESSION_LESS_OR_EQUAL,
      [EXPRESSION_GET, OMT_RANK_FIELD],
      AMBIENT_POI_MAX_RANK,
    ],
  ];

  if (authoredNames.length > 0) {
    // Both name fields, because the pin's label may match either the tiles'
    // own name or the latin transliteration the label layer prefers.
    [OMT_NAME_FIELD, OMT_NAME_LATIN_FIELD].forEach((field) => {
      clauses.push([
        EXPRESSION_NOT,
        [
          EXPRESSION_IN,
          [EXPRESSION_GET, field],
          [EXPRESSION_LITERAL, authoredNames],
        ],
      ]);
    });
  }

  return [EXPRESSION_ALL, ...clauses] as unknown as FilterSpec;
}

/** A zoom ramp, spelled once for the three properties that take one. */
function zoomRamp(
  nearValue: number,
  farValue: number,
  farZoom: number,
): StyleExpression {
  return [
    EXPRESSION_INTERPOLATE,
    [EXPRESSION_LINEAR],
    [EXPRESSION_ZOOM],
    AMBIENT_POI_MIN_ZOOM,
    nearValue,
    farZoom,
    farValue,
  ] as StyleExpression;
}

/** Which dot a feature draws: one neutral dot, or one per category. */
function iconImageExpression(
  tintsByCategory: boolean,
): StyleExpression | string {
  if (!tintsByCategory) {
    return dotImageId(null);
  }

  const branches = Object.entries(CLASSES_BY_CATEGORY).flatMap(
    ([category, classNames]) => [
      classNames,
      dotImageId(category as MapPoiCategory),
    ],
  );

  // The style spec allows a `match` label to be an ARRAY of values (one branch
  // serving a whole category), and MapLibre evaluates it — but the published
  // `ExpressionSpecification` type models only scalar labels, so the assertion
  // is the type gap, not a claim about the runtime. `map-ambient-poi.assert.ts`
  // pins the shape this builds, and the live map is what proves it evaluates.
  return [
    EXPRESSION_MATCH,
    [EXPRESSION_GET, OMT_CLASS_FIELD],
    ...branches,
    dotImageId(null),
  ] as unknown as StyleExpression;
}

/** The name, preferring the latin transliteration the style itself prefers. */
function labelExpression(): StyleExpression {
  return [
    EXPRESSION_COALESCE,
    [EXPRESSION_GET, OMT_NAME_LATIN_FIELD],
    [EXPRESSION_GET, OMT_NAME_FIELD],
  ] as StyleExpression;
}

interface AmbientPoiLayerInput {
  sourceId: string;
  variant: MapAmbientPoiVariant;
  /** Labels of the pins the block itself authored, so the basemap's own copy
   *  of the same place stays out of the picture. */
  authoredNames?: readonly string[];
}

export function buildAmbientPoiLayer({
  sourceId,
  variant,
  authoredNames = [],
}: AmbientPoiLayerInput): SymbolLayer {
  const drawsLabels = VARIANT_DRAWS_LABELS[variant];
  const layout: SymbolLayer['layout'] = {
    'icon-image': iconImageExpression(VARIANT_TINTS_BY_CATEGORY[variant]),
    'icon-size': zoomRamp(0.85, 1.1, 17),
    // Collision is the whole density strategy: dots that cannot fit are
    // dropped, and the sort key decides which survive.
    'icon-allow-overlap': false,
    'symbol-sort-key': [EXPRESSION_GET, OMT_RANK_FIELD] as StyleExpression,
  };

  if (drawsLabels) {
    layout['text-field'] = labelExpression();
    layout['text-font'] = [...AMBIENT_LABEL_FONT];
    layout['text-size'] = zoomRamp(10, 11.5, 17);
    layout['text-anchor'] = AMBIENT_LABEL_ANCHOR;
    layout['text-offset'] = [...AMBIENT_LABEL_OFFSET];
    layout['text-max-width'] = AMBIENT_LABEL_MAX_WIDTH_EMS;
    // A dot whose name will not fit still marks its place.
    layout['text-optional'] = true;
  }

  return {
    id: MAP_AMBIENT_POI_LAYER_ID,
    type: LAYER_TYPE_SYMBOL,
    source: sourceId,
    [SOURCE_LAYER_PROPERTY]: OMT_POI_SOURCE_LAYER,
    minzoom: AMBIENT_POI_MIN_ZOOM,
    filter: ambientPoiFilter(authoredNames),
    layout,
    paint: {
      'icon-opacity': zoomRamp(0.75, 1, 16),
      'text-color': AMBIENT_LABEL_COLOR,
      'text-halo-color': AMBIENT_LABEL_HALO_COLOR,
      'text-halo-width': AMBIENT_LABEL_HALO_WIDTH,
    },
  } as SymbolLayer;
}

/* ── Applying it to a live map ────────────────────────────────────────────── */

/** The dot bitmap, in device pixels. Drawn at 2x and registered at that ratio,
 *  so it stays crisp on a retina canvas. */
const DOT_PIXEL_RATIO = 2;
const DOT_CANVAS_SIZE = 20;
const DOT_RADIUS = 4;
const DOT_RIM_WIDTH = 1.5;
const CANVAS_2D_CONTEXT = '2d';

function dotImageData(color: string): ImageData | null {
  const canvas = document.createElement('canvas');
  canvas.width = DOT_CANVAS_SIZE;
  canvas.height = DOT_CANVAS_SIZE;
  const context = canvas.getContext(CANVAS_2D_CONTEXT);
  if (!context) {
    return null;
  }

  const centre = DOT_CANVAS_SIZE / 2;
  const radius = DOT_RADIUS * DOT_PIXEL_RATIO;
  context.beginPath();
  context.arc(centre, centre, radius, 0, Math.PI * 2);
  context.fillStyle = color;
  context.fill();
  context.lineWidth = DOT_RIM_WIDTH * DOT_PIXEL_RATIO;
  context.strokeStyle = AMBIENT_DOT_RIM_COLOR;
  context.stroke();

  return context.getImageData(0, 0, DOT_CANVAS_SIZE, DOT_CANVAS_SIZE);
}

/** Registers the dots this variant needs. A style swap clears a map's images,
 *  so this re-runs with the layer and skips whatever survived. */
function ensureDotImages(
  map: MapLibreMapInstance,
  variant: MapAmbientPoiVariant,
): void {
  const wanted: (MapPoiCategory | null)[] = VARIANT_TINTS_BY_CATEGORY[variant]
    ? [null, ...Object.values(MapPoiCategory)]
    : [null];

  wanted.forEach((category) => {
    const id = dotImageId(category);
    if (map.hasImage(id)) {
      return;
    }
    const color =
      category == null
        ? AMBIENT_DOT_NEUTRAL_COLOR
        : AMBIENT_DOT_COLOR_BY_CATEGORY[category];
    const image = dotImageData(color);
    if (image) {
      map.addImage(id, image, { pixelRatio: DOT_PIXEL_RATIO });
    }
  });
}

/**
 * Reading the style is the same guarded call the cartography makes: before the
 * style has parsed, `getStyle()` answers with something whose `layers`
 * dereference throws, and this absorbs it so a caller can run unconditionally.
 */
function readStyleLayers(map: MapLibreMapInstance): readonly StyleLayer[] {
  try {
    return map.getStyle().layers;
  } catch {
    return [];
  }
}

/**
 * The vector source the OMT layers already use, read off the style rather than
 * hardcoded: a style is free to name its source anything, and the `poi` layer
 * lives in whichever source the `place` layers came from.
 */
function omtSourceId(layers: readonly StyleLayer[]): string | null {
  // `source` is absent from a background layer outright, so both the guard and
  // the read go through the `in` narrowing rather than a property access the
  // union does not carry.
  for (const layer of layers) {
    if (
      !(SOURCE_LAYER_PROPERTY in layer) ||
      layer[SOURCE_LAYER_PROPERTY] !== OMT_PLACE_SOURCE_LAYER ||
      !('source' in layer)
    ) {
      continue;
    }
    const source: unknown = layer.source;
    if (typeof source === 'string') {
      return source;
    }
  }

  return null;
}

/**
 * Where the layer is inserted — which decides what it is allowed to push off
 * the map, and it is not a cosmetic choice.
 *
 * MapLibre runs symbol placement from the TOPMOST symbol layer downward, so a
 * layer inserted higher wins every collision against the ones below it.
 * Anchored above the street names (before the first `place` layer), ambient
 * POIs displaced 18 of 19 street labels in a live Kreuzberg frame — the map
 * lost the names a reader orients by in order to show the shops between them.
 *
 * So the anchor is the first `transportation_name` layer instead: street names
 * and every place label sit ABOVE ambient POIs and win, and ambient POIs fill
 * the space those leave. The `place` layer is the fallback for a style that
 * labels no roads, and undefined — appending on top — is the last resort for a
 * style with no labels at all to lose.
 */
function ambientPoiAnchorId(layers: readonly StyleLayer[]): string | undefined {
  const anchorBySourceLayer = (sourceLayer: string): string | undefined =>
    layers.find(
      (layer) =>
        layer.type === LAYER_TYPE_SYMBOL &&
        SOURCE_LAYER_PROPERTY in layer &&
        layer[SOURCE_LAYER_PROPERTY] === sourceLayer,
    )?.id;

  return (
    anchorBySourceLayer(OMT_TRANSPORTATION_NAME_SOURCE_LAYER) ??
    anchorBySourceLayer(OMT_PLACE_SOURCE_LAYER)
  );
}

/** MapLibre throws on a layer id that is already taken and on removing one
 *  that is not there; both are states this function is expected to walk into,
 *  because it re-runs on every style parse and every variant change. */
function tryLayer(apply: () => void): void {
  try {
    apply();
  } catch {
    // The style is mid-swap or already holds our layer; the next call settles it.
  }
}

export function removeMapAmbientPoi(map: MapLibreMapInstance): void {
  if (map.getLayer(MAP_AMBIENT_POI_LAYER_ID)) {
    tryLayer(() => {
      map.removeLayer(MAP_AMBIENT_POI_LAYER_ID);
    });
  }
}

/**
 * Draws (or clears) the ambient POI layer.
 *
 * Idempotent and re-runnable, exactly like `applyMapCartography`: it removes
 * its own layer first, so a variant change, a style that parsed late and a
 * second call on a settled map all converge on the same single layer.
 */
export function applyMapAmbientPoi(
  map: MapLibreMapInstance,
  variant: MapAmbientPoiVariant,
  authoredNames: readonly string[] = [],
): void {
  removeMapAmbientPoi(map);
  if (variant === MapAmbientPoiVariant.Off) {
    return;
  }

  const layers = readStyleLayers(map);
  if (layers.length === 0) {
    return;
  }

  const sourceId = omtSourceId(layers);
  if (sourceId == null) {
    return;
  }

  ensureDotImages(map, variant);
  tryLayer(() => {
    map.addLayer(
      buildAmbientPoiLayer({ sourceId, variant, authoredNames }),
      ambientPoiAnchorId(layers),
    );
  });
}
